(function () {
  if (customElements.get('smallsmore-button-set')) return;

  class SmallsmoreButtonSet extends HTMLElement {
    connectedCallback() {
      this.currency = this.dataset.currency || 'USD';
      this.singlePriceCents = Number(this.dataset.singlePriceCents || 199);
      this.packageButtons = Array.from(this.querySelectorAll('[data-button-set-package]'));
      this.colorButtons = Array.from(this.querySelectorAll('[data-button-set-color]'));
      this.selectedColors = [];
      this.activePreviewColorId = '';
      this.nodes = {
        mediaStack: this.querySelector('.smallsmore-button-set__media-stack'),
        variantId: this.querySelector('[data-button-set-variant-id]'),
        price: this.querySelector('[data-button-set-price]'),
        savings: this.querySelector('[data-button-set-savings]'),
        inventory: this.querySelector('[data-button-set-inventory]'),
        inventoryText: this.querySelector('[data-button-set-inventory-text]'),
        count: this.querySelector('[data-button-set-count]'),
        preview: this.querySelector('[data-button-set-preview]'),
        mainImage: this.querySelector('[data-button-set-main-image]'),
        thumbnailList: this.querySelector('[data-button-set-thumbnail-list]'),
        status: this.querySelector('[data-button-set-status]'),
        form: this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form[action*="/cart/add"]'),
        submit: this.querySelector('[data-button-set-submit]'),
        submitText: this.querySelector('[data-button-set-submit] span'),
        loadingSpinner: this.querySelector('[data-button-set-submit] .loading__spinner'),
        propertyColors: this.querySelector('[data-button-set-property-colors]'),
        propertySize: this.querySelector('[data-button-set-property-size]'),
        propertySavings: this.querySelector('[data-button-set-property-savings]'),
        specBlock: this.querySelector('[data-button-set-spec-block]'),
        specToggle: this.querySelector('[data-button-set-spec-toggle]'),
        setStep: this.querySelector('[data-button-set-step="set"]')
      };

      if (!this.packageButtons.length || !this.nodes.variantId) return;

      this.submitLabel = this.nodes.submit?.dataset.buttonSetSubmitLabel || 'Add to Cart';
      this.packageButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectPackage(button));
      });
      this.colorButtons.forEach((button) => {
        button.addEventListener('click', () => this.toggleColor(button));
      });
      if (this.nodes.specToggle && this.nodes.specBlock) {
        this.nodes.specToggle.addEventListener('click', () => this.toggleSpecBlock());
      }
      if (this.nodes.form) {
        this.nodes.form.addEventListener('submit', (event) => this.onSubmitHandler(event), { capture: true });
      }

      const initialPackage =
        this.packageButtons.find((button) => this.getPackageSetSize(button) === 1) ||
        this.packageButtons.find((button) => button.classList.contains('is-active')) ||
        this.packageButtons[0];
      this.selectPackage(initialPackage, { preserveColors: false, sync: false });
      this.selectDefaultColor();
      this.updatePrice();
      this.syncState();
    }

    toggleSpecBlock() {
      const expanded = !this.nodes.specBlock.classList.contains('is-expanded');
      this.nodes.specBlock.classList.toggle('is-expanded', expanded);
      this.nodes.specToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.nodes.specToggle.setAttribute(
        'aria-label',
        expanded ? 'Show fewer technical details' : 'Show all technical details'
      );
    }

    selectPackage(button, options = {}) {
      if (!button) return;
      const preserveColors = options.preserveColors !== false;
      const sync = options.sync !== false;

      this.packageButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      this.activePackage = button;
      this.setSize = this.getActiveSetSize();
      this.variantAvailable = button.dataset.available !== 'false';
      this.nodes.variantId.value = button.dataset.variantId || '';
      this.nodes.variantId.disabled = !this.variantAvailable;

      if (!preserveColors) {
        this.selectedColors = [];
        this.colorButtons.forEach((item) => this.setColorActive(item, false));
      } else {
        this.trimSelectedColors();
      }
      if (this.isCompleteColorSet()) {
        this.selectAllColors();
      }

      if (sync) {
        this.updatePrice();
        this.syncState();
      }
    }

    toggleColor(button) {
      if (!button || !this.activePackage) return;
      if (this.isCompleteColorSet()) {
        this.selectAllColors();
        this.syncState();
        return;
      }

      const isActive = button.classList.contains('is-active');

      if (isActive) {
        this.selectedColors = this.selectedColors.filter((item) => item !== button);
        this.setColorActive(button, false);
        this.syncState();
        return;
      }

      if (this.getActiveSetSize() === 1) {
        this.selectedColors.forEach((item) => this.setColorActive(item, false));
        this.selectedColors = [button];
        this.setColorActive(button, true);
        this.activePreviewColorId = this.getColorId(button);
        this.syncState();
        return;
      }

      if (this.selectedColors.length >= this.getActiveSetSize()) {
        const replacementIndex = this.getActivePreviewIndex();
        const targetIndex = replacementIndex >= 0 ? replacementIndex : this.selectedColors.length - 1;
        const replacedButton = this.selectedColors[targetIndex];

        if (replacedButton) {
          this.setColorActive(replacedButton, false);
        }
        this.selectedColors[targetIndex] = button;
        this.setColorActive(button, true);
        this.activePreviewColorId = this.getColorId(button);
        this.syncState();
        return;
      }

      this.selectedColors.push(button);
      this.setColorActive(button, true);
      this.activePreviewColorId = this.getColorId(button);
      this.syncState();
    }

    setColorActive(button, active) {
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    selectDefaultColor() {
      const defaultButton = this.getDefaultColorButton();
      if (!defaultButton) return;

      this.selectedColors = [defaultButton];
      this.colorButtons.forEach((button) => {
        this.setColorActive(button, button === defaultButton);
      });
      this.activePreviewColorId = this.getColorId(defaultButton);
    }

    selectAllColors() {
      this.selectedColors = this.colorButtons.slice(0, this.getActiveSetSize());
      this.colorButtons.forEach((button) => {
        this.setColorActive(button, this.selectedColors.includes(button));
      });
      if (!this.getActivePreviewButton()) {
        this.activePreviewColorId = this.getColorId(this.selectedColors[0]);
      }
    }

    removeSelectedColor(button) {
      if (!button) return;

      const wasActivePreview = this.getColorId(button) === this.activePreviewColorId;

      this.selectedColors = this.selectedColors.filter((item) => item !== button);
      this.setColorActive(button, false);
      if (wasActivePreview) {
        this.activePreviewColorId = this.getColorId(this.selectedColors[this.selectedColors.length - 1]);
      }
      this.syncState();
    }

    trimSelectedColors() {
      const allowed = this.getActiveSetSize();
      const trimmed = this.selectedColors.slice(0, allowed);
      const trimmedSet = new Set(trimmed);

      this.selectedColors.forEach((button) => {
        if (!trimmedSet.has(button)) this.setColorActive(button, false);
      });
      this.selectedColors = trimmed;
      if (!trimmedSet.has(this.getActivePreviewButton())) {
        this.activePreviewColorId = this.getColorId(this.selectedColors[0]);
      }
    }

    getActiveSetSize() {
      return this.getPackageSetSize(this.activePackage);
    }

    getPackageSetSize(button) {
      const size = Number(button?.dataset.setSize || 1);
      return Number.isFinite(size) && size > 0 ? size : 1;
    }

    isCompleteColorSet() {
      return this.colorButtons.length > 0 && this.getActiveSetSize() >= this.colorButtons.length;
    }

    getActiveSavingCents() {
      const saving = Number(this.activePackage?.dataset.savingCents || 0);
      return Number.isFinite(saving) ? Math.max(0, saving) : 0;
    }

    getSelectedColors() {
      return this.selectedColors.filter((button) => button.classList.contains('is-active'));
    }

    getColorId(button) {
      return button?.dataset.colorId || button?.dataset.colorLabel || '';
    }

    getDefaultColorButton() {
      const defaultColorId = this.nodes.preview?.dataset.defaultColorId || 'black';
      return this.colorButtons.find((button) => this.getColorId(button) === defaultColorId) || this.colorButtons[0];
    }

    getActivePreviewButton() {
      return this.getSelectedColors().find((button) => this.getColorId(button) === this.activePreviewColorId);
    }

    getActivePreviewIndex() {
      return this.selectedColors.findIndex((button) => this.getColorId(button) === this.activePreviewColorId);
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return `${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.currency
        }).format(amount)} ${this.currency}`;
      } catch (error) {
        return `$${amount.toFixed(2)} ${this.currency}`;
      }
    }

    getRoute(path, fallback) {
      return window.routes && window.routes[path] ? window.routes[path] : fallback;
    }

    getPreviewThemeIdForRedirect(currentUrl = new URL(window.location.href)) {
      const explicitThemeId = currentUrl.searchParams.get('preview_theme_id');
      if (explicitThemeId) return explicitThemeId;

      const shopifyTheme = window.Shopify && window.Shopify.theme ? window.Shopify.theme : null;
      const themeId = shopifyTheme && shopifyTheme.id ? String(shopifyTheme.id) : '';
      const themeRole = shopifyTheme && shopifyTheme.role ? String(shopifyTheme.role) : '';

      return themeId && themeRole !== 'main' ? themeId : '';
    }

    getCartUrlWithPreviewParams() {
      const cartUrl = new URL(this.getRoute('cart_url', '/cart'), window.location.origin);
      const currentUrl = new URL(window.location.href);
      const previewThemeId = this.getPreviewThemeIdForRedirect(currentUrl);

      ['preview_theme_id', '_fd', 'pb'].forEach((name) => {
        if (currentUrl.searchParams.has(name)) {
          cartUrl.searchParams.set(name, currentUrl.searchParams.get(name));
        }
      });
      if (previewThemeId) cartUrl.searchParams.set('preview_theme_id', previewThemeId);

      return cartUrl;
    }

    redirectToCart() {
      window.location.href = this.getCartUrlWithPreviewParams().toString();
    }

    isReadyToSubmit() {
      if (!this.nodes.variantId || this.nodes.variantId.disabled || !this.nodes.variantId.value) return false;
      return this.variantAvailable && this.getSelectedColors().length === this.getActiveSetSize();
    }

    setSubmitLoading(loading) {
      if (!this.nodes.submit) return;

      this.nodes.submit.classList.toggle('loading', loading);
      if (loading) {
        this.nodes.submit.disabled = true;
        this.nodes.submit.setAttribute('aria-disabled', 'true');
      }
      if (this.nodes.loadingSpinner) {
        this.nodes.loadingSpinner.classList.toggle('hidden', !loading);
      }
    }

    async addCurrentFormToCart() {
      const response = await fetch(this.getRoute('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(this.nodes.form)
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || (data && data.status)) {
        const message = data && (data.description || data.message)
          ? data.description || data.message
          : 'The button set could not be added to cart.';
        throw new Error(message);
      }

      return data;
    }

    async onSubmitHandler(event) {
      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      if (this.isSubmitting) return;

      this.syncState();
      if (!this.isReadyToSubmit()) return;

      this.isSubmitting = true;
      this.setSubmitLoading(true);
      this.setStatus('Adding your button set to cart...');

      try {
        await this.addCurrentFormToCart();
        this.redirectToCart();
      } catch (error) {
        this.isSubmitting = false;
        this.setSubmitLoading(false);
        this.syncState();
        this.setStatus(error && error.message ? error.message : 'The button set could not be added to cart.');
      }
    }

    updatePrice() {
      if (!this.activePackage) return;

      if (this.nodes.price) {
        this.nodes.price.textContent = this.formatMoney(this.activePackage.dataset.priceCents);
      }

      const saving = this.getActiveSavingCents();
      if (this.nodes.savings) {
        this.nodes.savings.textContent = saving > 0
          ? `You save ${this.formatMoney(saving)} vs single sets.`
          : 'Single set price.';
      }

      if (this.nodes.inventory) {
        this.nodes.inventory.hidden = !this.variantAvailable;
      }
      if (this.nodes.inventoryText) {
        this.nodes.inventoryText.textContent = this.variantAvailable ? 'In stock' : 'Sold out';
      }
    }

    syncState() {
      const setSize = this.getActiveSetSize();
      const selected = this.getSelectedColors();
      this.selectedColors = selected;

      const selectedCount = selected.length;
      const remaining = Math.max(0, setSize - selectedCount);
      const ready = this.variantAvailable && remaining === 0;

      if (this.nodes.count) {
        this.nodes.count.textContent = `${selectedCount} / ${setSize} selected`;
      }

      if (this.nodes.propertyColors) {
        this.nodes.propertyColors.value = selected.map((button) => button.dataset.colorLabel || '').filter(Boolean).join(', ');
      }
      if (this.nodes.propertySize) {
        this.nodes.propertySize.value = String(setSize);
      }
      if (this.nodes.propertySavings) {
        this.nodes.propertySavings.value = this.formatMoney(this.getActiveSavingCents());
      }

      this.syncPreview(selected);

      if (!this.variantAvailable) {
        this.setStatus('This set is currently sold out.');
      } else if (remaining > 0) {
        this.setStatus(`Select ${remaining} more ${remaining === 1 ? 'color' : 'colors'} for this set.`);
      } else {
        this.setStatus(`Ready with ${setSize} ${setSize === 1 ? 'color' : 'colors'}.`);
      }

      this.setSubmitAvailability(ready, remaining);
    }

    syncPreview(selected) {
      const activeButton = this.getPreviewButton(selected);
      this.updateMainPreview(activeButton);
      this.renderPreviewThumbnails(selected, this.getActiveSetSize());
    }

    getPreviewButton(selected) {
      if (!selected.length) {
        this.activePreviewColorId = '';
        return this.getDefaultColorButton();
      }

      const activeButton = selected.find((button) => this.getColorId(button) === this.activePreviewColorId) || selected[0];
      this.activePreviewColorId = this.getColorId(activeButton);
      return activeButton;
    }

    updateMainPreview(button) {
      if (!this.nodes.mainImage || !button) return;

      const imageUrl = button.dataset.colorImage || '';
      if (imageUrl && this.nodes.mainImage.getAttribute('src') !== imageUrl) {
        this.nodes.mainImage.src = imageUrl;
      }
      this.nodes.mainImage.alt = `${button.dataset.colorLabel || 'Selected'} button set preview`;
    }

    renderPreviewThumbnails(selected, setSize) {
      if (!this.nodes.thumbnailList) return;

      const thumbnailCount = Math.max(1, setSize);
      const thumbnailGap = thumbnailCount >= 10 ? 0.45 : thumbnailCount >= 6 ? 0.6 : 1;
      const thumbnailMaxWidth = (thumbnailCount * 8.4) + ((thumbnailCount - 1) * thumbnailGap) + 1;
      const showRemoveButtons = selected.length >= setSize && setSize > 1 && !this.isCompleteColorSet();

      this.nodes.thumbnailList.dataset.thumbnailCount = String(thumbnailCount);
      this.nodes.thumbnailList.style.setProperty('--button-set-thumbnail-count', String(thumbnailCount));
      this.nodes.thumbnailList.style.setProperty('--button-set-thumbnail-gap', `${thumbnailGap}rem`);
      this.nodes.thumbnailList.style.setProperty('--button-set-thumbnail-max-width', `${thumbnailMaxWidth}rem`);
      this.nodes.thumbnailList.replaceChildren();
      Array.from({ length: thumbnailCount }).forEach((_, index) => {
        const button = selected[index];
        const item = document.createElement('li');

        item.className = 'smallsmore-button-set__thumbnail-item';

        if (!button) {
          const placeholder = document.createElement('span');

          item.classList.add('smallsmore-button-set__thumbnail-item--empty');
          placeholder.className = 'smallsmore-button-set__thumbnail-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          item.append(placeholder);
          this.nodes.thumbnailList.append(item);
          return;
        }

        const colorId = this.getColorId(button);
        const label = button.dataset.colorLabel || 'Selected';
        const imageUrl = button.dataset.colorImage || '';
        const thumbnailImageUrl = button.dataset.colorThumbnailImage || imageUrl;
        const active = colorId === this.activePreviewColorId;
        const thumbnailButton = document.createElement('button');
        const image = document.createElement('img');

        thumbnailButton.className = 'smallsmore-button-set__thumbnail';
        thumbnailButton.type = 'button';
        thumbnailButton.dataset.buttonSetThumbnail = '';
        thumbnailButton.dataset.colorId = colorId;
        thumbnailButton.setAttribute('aria-label', `Show ${label} button set preview`);
        thumbnailButton.classList.toggle('is-active', active);
        if (active) {
          thumbnailButton.setAttribute('aria-current', 'true');
        }

        image.src = thumbnailImageUrl;
        image.alt = `${label} button set thumbnail`;
        image.width = 416;
        image.height = 416;
        image.loading = 'lazy';

        thumbnailButton.append(image);
        thumbnailButton.addEventListener('click', () => {
          this.activePreviewColorId = colorId;
          this.syncPreview(this.getSelectedColors());
        });
        item.append(thumbnailButton);
        if (showRemoveButtons) {
          const removeButton = document.createElement('button');

          removeButton.className = 'smallsmore-button-set__thumbnail-remove';
          removeButton.type = 'button';
          removeButton.textContent = 'x';
          removeButton.setAttribute('aria-label', `Remove ${label} from this set`);
          removeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.removeSelectedColor(button);
          });
          item.append(removeButton);
        }
        this.nodes.thumbnailList.append(item);
      });
    }

    setSubmitAvailability(ready, remaining) {
      if (!this.nodes.submit) return;

      this.nodes.submit.disabled = !ready;
      this.nodes.submit.setAttribute('aria-disabled', ready ? 'false' : 'true');

      if (!this.nodes.submitText) return;
      if (ready) {
        this.nodes.submitText.textContent = this.submitLabel;
      } else if (!this.variantAvailable) {
        this.nodes.submitText.textContent = 'Sold out';
      } else {
        this.nodes.submitText.textContent = `Select ${remaining} ${remaining === 1 ? 'color' : 'colors'}`;
      }
    }

    setStatus(message) {
      if (this.nodes.status) this.nodes.status.textContent = message;
    }
  }

  customElements.define('smallsmore-button-set', SmallsmoreButtonSet);
})();
